import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate } from "react-router-dom";

export interface Breadcrumb {
  /** Visible crumb text. */
  label: string;
  /** In-app route the crumb points to. */
  to: string;
}

/**
 * Renders breadcrumb links into the `s-page` `breadcrumb-actions` slot.
 *
 * `s-link` requires an `href`, so we set the real in-app path — this keeps
 * the crumb a proper, accessible link (right-click / open-in-new-tab work)
 * and is the correct destination if a full navigation ever happens. We also
 * intercept the click to navigate via react-router instead of triggering a
 * full page reload, which would re-bootstrap the embedded app and drop state.
 * The NavigationBlocker still guards unsaved changes on either path.
 *
 * Must be rendered as a DIRECT child of `<s-page>` (it returns a fragment of
 * slotted `<s-link>`s with no wrapper element) so the slot projection works.
 */
export function PageBreadcrumbs({ items }: { items: Breadcrumb[] }) {
  const navigate = useNavigate();

  return (
    <>
      {items.map((item) => (
        <s-link
          key={item.to}
          slot="breadcrumb-actions"
          href={item.to}
          onClick={(e: CallbackEvent<"s-link">) => {
            e.preventDefault();
            navigate(item.to);
          }}
        >
          {item.label}
        </s-link>
      ))}
    </>
  );
}
